import { FC, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  TableColumnType,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import { DeleteOutlined, InfoCircleFilled } from '@ant-design/icons';
import { formatCurrency, formatNPWPValue } from '@/libs/antd/formater';
import FleetTitle from '../../../libs/antd/components/Title';
import dayjs from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createCustomerGroup,
  CreateCustomerGroupPayload,
  CustomerGroupMember,
  BmppGroupRelationship,
  MemberType,
} from '../../../libs/fleet-api/services/customer';
import { useApp } from '../../../App';
import type { PageName } from '../../../App';
import { useModals } from '@/components/modals/ModalProvider';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { FleetFile } from '../../../libs/fleet-api/fetcher';
import CustomerGroupAddMemberModal from './CustomerGroupAddMemberModal';
import ANTD_DEFAULT_PROPS from '../../../libs/antd/props';
import { uploader } from '../../../libs/fleet-api/uploader';
import blobToBase64 from '../../../utils/blob-to-base64';

type CustomerMemberDisplay = Omit<CustomerGroupMember, 'memberType'> & {
  customerName?: string;
  npwpNo?: string;
  memberType?: MemberType;
};

const MEMBER_TYPE_SHORT: Record<string, string> = {
  estar: 'E',
  corporate: 'C',
  retail: 'R',
  local: 'L',
};

const MEMBER_TYPE_COLOR: Record<string, string> = {
  estar: '#1557BC',
  local: '#C59B04',
};

type FormValues = {
  groupName: string;
  groupAddress: string;
  startDate?: { format: (fmt: string) => string };
  notes?: string;
};

type CreateCustomerGroupPageProps = {
  returnPage?: PageName;
};

const CreateCustomerGroupPage: FC<CreateCustomerGroupPageProps> = ({ returnPage = 'FRMCustomerGroupListPage' }) => {
  const { navigate, message } = useApp();
  const { openCustomerDetail } = useModals();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [members, setMembers] = useState<CustomerMemberDisplay[]>([]);
  const [treeFile, setTreeFile] = useState<FleetFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deletingMemberIndex, setDeletingMemberIndex] = useState<number | null>(null);
  const [pendingPayload, setPendingPayload] = useState<Parameters<typeof createCustomerGroup>[0] | null>(null);

  const createMutation = useMutation({
    mutationFn: createCustomerGroup,
    onSuccess: () => {
      message.success('Group BMPP created successfully');
      queryClient.invalidateQueries({ queryKey: ['getCustomerGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getCustomerGroupDetail'] });
      queryClient.invalidateQueries({ queryKey: ['getCustomerGroupMembers'] });
      navigate(returnPage);
    },
    onError: (error: unknown) => {
      const errMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(errMsg || 'Failed to create Group BMPP');
    },
  });

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields() as FormValues;
      if (members.length === 0) {
        message.error('Mohon Member of Group Customer BMPP dipilih terlebih dahulu');
        return;
      }
      if (members.some((m) => m.relationshipId == null)) {
        message.error(
          'Mohon Kelompok BMPP untuk semua member dilengkapi terlebih dahulu',
        );
        return;
      }
      if (!treeFile) {
        message.error('Please upload Company Tree');
        return;
      }
      const payload = {
        name: values.groupName,
        address: values.groupAddress,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
        isActive: true,
        notes: values.notes,
        companyTreeFile: {
          id: String(treeFile.id),
          name: treeFile.name,
          path: treeFile.path ?? '',
        },
        members: members.map((m) => ({
          customerId: m.customerId,
          relationshipId: m.relationshipId,
          memberType: m.memberType ?? 'local',
        })),
      } satisfies CreateCustomerGroupPayload;
      setPendingPayload(payload);
      setShowConfirm(true);
    } catch {
      // validation failed, antd handles field errors
    }
  };

  const columns: TableColumnType<CustomerMemberDisplay>[] = [
    {
      title: 'Action',
      align: 'center',
      render: (_, __, index) => (
        <DeleteOutlined
          style={{ color: '#1557BC', cursor: 'pointer' }}
          onClick={() => {
            setDeletingMemberIndex(index);
          }}
        />
      ),
    },
    {
      title: 'Customer ID',
      dataIndex: 'customerId',
    },
    {
      title: 'Nama Customer',
      render: (_, record) =>
        record.customerId ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              openCustomerDetail(String(record.customerId));
            }}
          >
            {record.customerName}
          </a>
        ) : (
          record.customerName ?? '-'
        ),
    },
    {
      title: 'NPWP',
      render: (_, record) =>
        record.npwpNo ? formatNPWPValue(record.npwpNo) : '-',
    },
    {
      title: 'Kelompok BMPP',
      dataIndex: 'relationshipId',
      onCell: (record) => ({
        style: record.relationshipId == null ? { backgroundColor: '#fff1f0' } : undefined,
      }),
      render: (val: BmppGroupRelationship | null) => {
        if (val == null) return '-';
        if (val === BmppGroupRelationship.Parent) return 'Pihak Terkait';
        if (val === BmppGroupRelationship.Subsidiary)
          return 'Bukan Pihak Terkait';
        if (val === BmppGroupRelationship.Affiliate)
          return 'Kelompok Bukan Pihak Terkait';
        return val;
      },
    },
    {
      title: 'Limit Pembiayaan JF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).jfFinancingLimitAmount
          ? formatCurrency((record as Record<string, unknown>).jfFinancingLimitAmount as number)
          : '-',
    },
    {
      title: 'Limit Pembiayaan NJF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).njfFinancingLimitAmount
          ? formatCurrency((record as Record<string, unknown>).njfFinancingLimitAmount as number)
          : '-',
    },
    {
      title: 'NTF JF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).jfNtfAmount
          ? formatCurrency((record as Record<string, unknown>).jfNtfAmount as number)
          : '-',
    },
    {
      title: 'NTF NJF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).njfNtfAmount
          ? formatCurrency((record as Record<string, unknown>).njfNtfAmount as number)
          : '-',
    },
    {
      title: 'OS Principal JF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).jfOutstandingPrincipalAmount
          ? formatCurrency((record as Record<string, unknown>).jfOutstandingPrincipalAmount as number)
          : '-',
    },
    {
      title: 'OS Principal NJF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).njfOutstandingPrincipalAmount
          ? formatCurrency((record as Record<string, unknown>).njfOutstandingPrincipalAmount as number)
          : '-',
    },
    {
      title: 'Sisa Limit JF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).jfRemainingLimitAmount
          ? formatCurrency((record as Record<string, unknown>).jfRemainingLimitAmount as number)
          : '-',
    },
    {
      title: 'Sisa Limit NJF',
      align: 'right',
      render: (_, record) =>
        (record as Record<string, unknown>).njfRemainingLimitAmount
          ? formatCurrency((record as Record<string, unknown>).njfRemainingLimitAmount as number)
          : '-',
    },
    {
      title: 'Sumber',
      align: 'center',
      render: (_, record) => {
        const typeKey = (record.memberType ?? '').toString().toLowerCase();
        const typeShort = MEMBER_TYPE_SHORT[typeKey];
        const typeColor = MEMBER_TYPE_COLOR[typeKey];
        if (!typeShort) return '-';
        return (
          <span style={{ fontWeight: 700, color: typeColor }}>
            {typeShort}
          </span>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ display: 'flex' }} size="large">
      <div>
        <FleetTitle>Add Group Customer BMPP</FleetTitle>
        <Form
          {...ANTD_DEFAULT_PROPS.FORM}
          form={form}
          // layout="horizontal"
          // labelCol={{ span: 4 }}
          // wrapperCol={{ span: 14 }}
        >
          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item
                label="Group Name"
                name="groupName"
                rules={[{ required: true, message: 'Please input Group Name' }]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
              >
                <Input placeholder="Input Group Name" onChange={(e) => form.setFieldValue('groupName', e.target.value.toUpperCase())} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[20, 20]}>
            <Col span={24}>
              <Form.Item
                label="Group Address"
                name="groupAddress"
                rules={[{ required: true, message: 'Please input Group Address' }]}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
              >
                <Input placeholder="Input Group Address" />
              </Form.Item>
            </Col>
          </Row>
          <Divider />

          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item
                label="Start Date"
                name="startDate"
                rules={[{ required: true, message: 'Please select Start Date' }]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf('day')
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Flex justify="space-between" align="center" style={{ width: '100%', marginBottom: 16 }}>
            <Typography.Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
              <strong>*Mohon Member of Group Customer BMPP dipilih terlebih dahulu</strong>
            </Typography.Text>
            <Button
              style={{
                color: '#1557BC',
                borderRadius: 50,
                fontSize: 12,
                height: 24,
                borderColor: '#1557BC',
                fontWeight: 700,
              }}
              onClick={() => setIsModalOpen(true)}
            >
              + Add Member
            </Button>
          </Flex>

          <Table
            bordered
            scroll={{ x: 'max-content' }}
            pagination={false}
            dataSource={members.map((m, key) => ({ ...m, key }))}
            columns={columns}
            style={{ marginBottom: 24 }}
          />
          {members.some((m) => m.relationshipId == null) && (
            <Alert
              type="error"
              showIcon
              message="Terdapat member yang belum memiliki Kelompok BMPP. Mohon hapus dan tambahkan kembali member dengan Kelompok BMPP yang sesuai."
              style={{ marginTop: 20, marginBottom: 24 }}
            />
          )}

          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item
                label="Upload Company Tree"
                required
                style={{ marginBottom: 24 }}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
              >
                <Upload.Dragger
                  listType="picture"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxCount={1}
                  fileList={treeFile?.id ? [{ uid: String(treeFile.id), name: treeFile.name, status: 'done', url: treeFile.url }] as UploadFile[] : []}
                  beforeUpload={async (file) => {
                    setIsUploading(true);
                    try {
                      const fileid = Date.now().toString();
                      const filepath = `customer-group/tree`;

                      await uploader({
                        file,
                        fileid,
                        filepath,
                      });

                      setTreeFile({
                        id: fileid,
                        name: file.name,
                        path: filepath,
                        url: await blobToBase64(file),
                      });
                    } catch {
                      message.error('Failed to upload file');
                    } finally {
                      setIsUploading(false);
                    }
                    return false; // Prevent auto-upload
                  }}
                  onRemove={() => setTreeFile(null)}
                >
                  {isUploading ? (
                    <LoadingOutlined style={{ fontSize: 25, color: '#BABABA', display: 'block' }} />
                  ) : (
                    <UploadOutlined style={{ fontSize: 25, color: '#BABABA', display: 'block' }} />
                  )}
                  {/* <p className="ant-upload-text">Click or drag file to this area to upload</p> */}
                </Upload.Dragger>
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Row gutter={[20, 20]}>
            <Col span={24}>
              <Form.Item label="Informasi Tambahan" name="notes" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} style={{ marginBottom: 40 }}>
                <Input.TextArea rows={4} placeholder="Input informasi tambahan" />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Col>
              <Button
                type="primary"
                onClick={handleSaveClick}
                loading={createMutation.isPending}
                disabled={members.some((m) => m.relationshipId == null)}
              >
                SAVE
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      <Modal
        open={deletingMemberIndex !== null}
        footer={null}
        onCancel={() => setDeletingMemberIndex(null)}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <InfoCircleFilled
            style={{ fontSize: 64, color: '#1557BC', marginBottom: 16 }}
          />
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 500,
              marginBottom: 32,
            }}
          >
            Apakah Anda yakin akan menghapus Customer berikut dari Member of Group Customer BMPP?
          </Typography.Text>
          <Space size={16}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => setDeletingMemberIndex(null)}
            >
              TIDAK
            </Button>
            <Button
              type="primary"
              style={{ minWidth: 120 }}
              onClick={() => {
                setMembers((prev) =>
                  prev.filter((_, i) => i !== deletingMemberIndex),
                );
                setDeletingMemberIndex(null);
              }}
            >
              YA
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal
        open={showConfirm}
        footer={null}
        onCancel={() => setShowConfirm(false)}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <InfoCircleFilled
            style={{ fontSize: 64, color: '#1557BC', marginBottom: 16 }}
          />
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 500,
              marginBottom: 32,
            }}
          >
            Apakah Anda yakin data yang diinputkan sudah benar?
          </Typography.Text>
          <Space size={16}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => setShowConfirm(false)}
            >
              TIDAK
            </Button>
            <Button
              type="primary"
              style={{ minWidth: 120 }}
              loading={createMutation.isPending}
              onClick={() => {
                if (pendingPayload) {
                  setShowConfirm(false);
                  createMutation.mutate(pendingPayload);
                }
              }}
            >
              YA
            </Button>
          </Space>
        </div>
      </Modal>

      <CustomerGroupAddMemberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        existingCustomerIds={members.map((m) => m.customerId ?? '').filter(Boolean)}
        onSelect={(member) => {
          // prevent duplicate
          if (!members.find((m) => m.customerId === member.customerId)) {
            setMembers((prev) => [...prev, { ...member, memberType: (member.memberType as MemberType) ?? 'local' }]);
          } else {
            message.warning('Customer is already added to the list.');
          }
        }}
      />
    </Space >
  );
};

export default CreateCustomerGroupPage;
